import { Component, OnInit, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { environment } from '../../services/global-environment';

interface UserProfileData {
  username: string;
  fullName: string;
  email: string;
  position: string;
}

interface PasswordChangeData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

@Component({
  selector: 'app-user-profile-edit',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './user-profile-edit.component.html',
  styleUrl: './user-profile-edit.component.scss'
})
export class UserProfileEditComponent implements OnInit {
  @Output() closeModal = new EventEmitter<void>();
  @Output() profileUpdated = new EventEmitter<void>();

  userProfileData: UserProfileData = {
    username: '',
    fullName: '',
    email: '',
    position: ''
  };

  passwordChangeData: PasswordChangeData = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  };

  showPasswordChangeModal = false;
  isSubmitting = false;
  isPasswordChanging = false;
  errorMessage = '';
  successMessage = '';

  private apiUrl = environment.apiUrl;

  constructor(
    private authService: AuthService,
    private i18nService: I18nService,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.loadUserProfile();
  }


   async loadUserProfile() {
    const userInfo = this.authService.getUserInfo();

    // Initialize with basic username from auth service
    this.userProfileData = {
      username: userInfo.username || '',
      fullName: '',
      email: '',
      position: ''
    };

    // Fetch user data from API
    try {
      const userId = localStorage.getItem('userid');
      const companyid = environment.companyid || localStorage.getItem('companyid');

      if (userId && companyid) {
        // Use dedicated user info API
        const requestData = {
          companyid: companyid,
          id: userId
        };

        const response = await this.http.post<any>(`${this.apiUrl}/api/login/get_user_info`, requestData, {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        }).toPromise();

        if (response && response.success && response.data) {
          const userData = response.data;
          this.userProfileData = {
            username: userData.username ||  '',
            fullName: userData.fullName || '',
            email: userData.email || '',
            position: userData.position || ''
          };

          // Store in localStorage for future use
          if (userData.fullName) localStorage.setItem('fullName', userData.fullName);
          if (userData.email) localStorage.setItem('email', userData.email);
          if (userData.position) localStorage.setItem('position', userData.position);
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      // Keep the initialized data if API call fails
    }
  }

  openPasswordChangeModal() {
    this.showPasswordChangeModal = true;
    this.passwordChangeData = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    this.errorMessage = '';
  }

  closePasswordChangeModal() {
    this.showPasswordChangeModal = false;
    this.passwordChangeData = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    this.errorMessage = '';
  }

  async verifyCurrentPassword(): Promise<boolean> {
    try {
      // Use dedicated password verification API
      const verifyData = {
        companyid: environment.companyid || localStorage.getItem('companyid'),
        username: this.userProfileData.username,
        password: this.passwordChangeData.currentPassword
      };

      const response = await this.http.post<any>(`${this.apiUrl}/api/login/verify_password`, verifyData, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      }).toPromise();

      return response && response.success && response.isValid;
    } catch (error) {
      console.error('Current password verification failed:', error);
      return false;
    }
  }

  async changePassword() {
    if (!this.validatePasswordChange()) {
      return;
    }

    this.isPasswordChanging = true;
    this.errorMessage = '';

    try {
      // Step 1: Verify current password using database query
      const isCurrentPasswordValid = await this.verifyCurrentPassword();

      if (!isCurrentPasswordValid) {
        this.errorMessage = this.i18nService.translate('user_profile.password_change.current_password_incorrect');
        this.isPasswordChanging = false;
        return;
      }

      // Step 2: Update password via user update API
      const userId = localStorage.getItem('userid');
      const companyid = environment.companyid || localStorage.getItem('companyid');

      const updateData = {
        id: userId,
        username: this.userProfileData.username,
        password: this.passwordChangeData.newPassword,
        fullName: this.userProfileData.fullName,
        email: this.userProfileData.email,
        position: this.userProfileData.position,
        companyid: companyid
      };

      const apiUrl = `${this.apiUrl}/api/users/updateuser?id=${encodeURIComponent(userId!)}&companyid=${encodeURIComponent(companyid!)}`;

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error (${response.status})`);
      }

      const data = await response.json();

      if (data.success) {
        this.successMessage = this.i18nService.translate('user_profile.password_change.success');
        this.closePasswordChangeModal();
        setTimeout(() => this.successMessage = '', 3000);
      } else {
        this.errorMessage = data.message || this.i18nService.translate('user_profile.password_change.update_failed');
      }
    } catch (error) {
      console.error('Password change failed:', error);
      this.errorMessage = this.i18nService.translate('user_profile.password_change.update_failed');
    } finally {
      this.isPasswordChanging = false;
    }
  }

  validatePasswordChange(): boolean {
    if (!this.passwordChangeData.currentPassword) {
      this.errorMessage = this.i18nService.translate('user_profile.password_change.current_password_required');
      return false;
    }

    if (!this.passwordChangeData.newPassword) {
      this.errorMessage = this.i18nService.translate('user_profile.password_change.new_password_required');
      return false;
    }

    if (this.passwordChangeData.newPassword.length < 6) {
      this.errorMessage = this.i18nService.translate('user_profile.password_change.new_password_min_length');
      return false;
    }

    if (this.passwordChangeData.newPassword !== this.passwordChangeData.confirmPassword) {
      this.errorMessage = this.i18nService.translate('user_profile.password_change.passwords_not_match');
      return false;
    }

    return true;
  }

  async saveProfile() {
    if (!this.validateProfile()) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    try {
      const userId = localStorage.getItem('userid');
      const companyid = environment.companyid || localStorage.getItem('companyid');

      const updateData = {
        id: userId,
        username: this.userProfileData.username,
        fullName: this.userProfileData.fullName,
        email: this.userProfileData.email,
        position: this.userProfileData.position,
        companyid: companyid
      };

      const apiUrl = `${this.apiUrl}/api/users/updateuser?id=${encodeURIComponent(userId!)}&companyid=${encodeURIComponent(companyid!)}`;

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error (${response.status})`);
      }

      const data = await response.json();

      if (data.success) {
        // Update localStorage with new values
        localStorage.setItem('fullName', this.userProfileData.fullName);
        localStorage.setItem('email', this.userProfileData.email);
        localStorage.setItem('position', this.userProfileData.position);

        this.successMessage = this.i18nService.translate('user_profile.update_success');
        this.profileUpdated.emit();

        setTimeout(() => {
          this.successMessage = '';
          this.closeModal.emit();
        }, 2000);
      } else {
        this.errorMessage = data.message || this.i18nService.translate('user_profile.update_failed');
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      this.errorMessage = this.i18nService.translate('user_profile.update_failed');
    } finally {
      this.isSubmitting = false;
    }
  }

  validateProfile(): boolean {
    if (this.userProfileData.email && !this.isValidEmail(this.userProfileData.email)) {
      this.errorMessage = this.i18nService.translate('user_profile.email_invalid');
      return false;
    }

    return true;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  onClose() {
    this.closeModal.emit();
  }
}
